/**
 * The shape of the search screen, before there is anything to put in it.
 *
 * Shown by BOTH `AutoEnroll` (while the trial is being provisioned) and `SearchPage` (until the
 * first page of results lands). That sharing is the entire point: the old sequence showed a
 * "פותחים לכם התנסות" message, then swapped in a differently-shaped empty page with placeholder
 * text, then filled it — three distinct screens for one continuous wait. Now the layout appears
 * once and its contents resolve in place.
 *
 * Provisioning is deliberately not announced. It is our bookkeeping, not something the user asked
 * for or can act on, and naming it invites them to wonder whether it might fail.
 */
/**
 * Just the sidebar and the results — used by `SearchPage`, whose real toolbar stays live above it.
 *
 * The toolbar is deliberately NOT skeletonised there: the search box and sort control work fine
 * before any results exist, and replacing them with grey blocks would take away the one thing a
 * user might want to do while waiting.
 */
export function ResultsSkeleton() {
  return (
    <>
      {/* One live region for the whole wait, so a screen reader hears "loading" once rather than
          narrating a wall of decorative blocks. */}
      <p className="huaca-visually-hidden" role="status">
        טוען…
      </p>
      <div className="tenders-layout" aria-hidden="true" data-testid="results-skeleton">
        <aside className="tenders-filters">
          {[0, 1, 2].map((group) => (
            <div key={group} className="tenders-facet">
              <span className="tenders-skel tenders-skel--legend" />
              {[0, 1, 2, 3].map((row) => (
                <span key={row} className="tenders-skel tenders-skel--row" />
              ))}
            </div>
          ))}
        </aside>

        <section className="tenders-results">
          <span className="tenders-skel tenders-skel--count" />
          <ul className="tenders-list">
            {[0, 1, 2, 3, 4].map((card) => (
              <li key={card}>
                <div className="tenders-card">
                  <span className="tenders-skel tenders-skel--title" />
                  <span className="tenders-skel tenders-skel--meta" />
                  <span className="tenders-skel tenders-skel--line" />
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}

/** The whole page, toolbar included — used by `AutoEnroll`, where nothing is interactive yet. */
export function SearchSkeleton() {
  return (
    <div className="tenders-page" data-testid="search-skeleton">
      <div className="tenders-toolbar" aria-hidden="true">
        <span className="tenders-skel tenders-skel--input" />
        <span className="tenders-skel tenders-skel--select" />
      </div>
      <ResultsSkeleton />
    </div>
  );
}

/**
 * The side panel, while the posting is being fetched.
 *
 * The panel is always a skeleton first, never a partial paint from the list row. It renders over a
 * search that may not contain the posting at all — a shared link lands on a list the reader has
 * never scrolled — so filling the header from a row that might not be there would work from the
 * list and not from a link, which is the worse of the two failures to have.
 */
export function DetailSkeleton() {
  return (
    <>
      {/* One live region for the whole wait, matching the results skeleton — a screen reader hears
          "loading" once rather than narrating a wall of decorative blocks. */}
      <p className="huaca-visually-hidden" role="status">
        טוען…
      </p>
      <div aria-hidden="true" data-testid="detail-skeleton">
        <span className="tenders-skel tenders-skel--legend" />
        {[0, 1, 2].map((group) => (
          <div key={group} className="tenders-detail__section">
            <span className="tenders-skel tenders-skel--legend" />
            <span className="tenders-skel tenders-skel--row" />
            <span className="tenders-skel tenders-skel--row" />
          </div>
        ))}
      </div>
    </>
  );
}
